/**
 * Folders in the items table: the first rows, each a link to its page and, by its chevron, open in place onto its
 * subfolders and items (the list's filters still on, as the tree's groups open onto theirs). A row says how many items
 * are in it (of those the list's filters match), who sees it when that isn't the space, and whether its sharing
 * differs from the folder it's in.
 */
import { useState } from "preact/hooks";
import { api, type Query } from "../../api/client.ts";
import type { Crumb, FolderList, FolderRow, ObjEntity, PageItemRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { folderHref } from "../../lib/folders.ts";
import { formatInstant, formatNumber, formatRelative } from "../../lib/format.ts";
import { spaceApi } from "../../lib/paths.ts";
import { useResource } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { location } from "../../lib/router.ts";
import { SELECTED_ROW, type Selection } from "../../lib/selection.ts";
import { titleOf } from "../../lib/titles.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Chevron } from "../../ui/Chevron.tsx";
import { Chip } from "../../ui/Chip.tsx";
import { FolderCommentCount } from "../../ui/counts.tsx";
import { FolderIcon } from "../../ui/icons.tsx";
import { Spinner } from "../../ui/Spinner.tsx";
import { SelectBox } from "../../ui/selection.tsx";
import { LINK } from "../../ui/styles.ts";
import { Td } from "../../ui/Table.tsx";
import { VisibilityMark } from "../item/visibility.tsx";
import { PeekButton } from "../items/icons.tsx";
import { TreeNode } from "../items/tree.tsx";
import { INDENT, SPAN, STEP } from "../items/treeLayout.ts";
import { Breadcrumb } from "./breadcrumb.tsx";
import { type Around, FolderDrift, ItemDrift } from "./drift.tsx";
import { DROP_OVER, dragIds, useDropInto } from "./drop.ts";
import { folderAsk } from "./listing.ts";

/** What folder rows show their contents with. */
interface Env {
  space: SpaceOut;
  entities: readonly ObjEntity[];
  /** The list's filters (as `GET /items` takes them), which a folder's contents meet too. */
  filters: Query;
  /** After a change in a row (sharing matched): the list reloads. */
  onChange: () => void;
  /** Filtered, folders holding no match listed too. */
  unmatched?: boolean;
  /** The list's selection: what's in an open folder is picked with the rest. */
  sel?: Selection | undefined;
}

/** A nested line's room before its own: the cell's edge and the checkboxes' column, then its level's indent. */
const padOf = (depth: number) => ({ paddingLeft: `${depth * INDENT + 2}rem` });

/**
 * What starts a line inside an open folder: its box (in the checkboxes' column, as the top rows' are) or the room for
 * one, then its level's indent.
 */
function Lead(p: { sel: Selection | undefined; id: string; title: string; depth: number }) {
  return (
    <>
      {p.sel ? <SelectBox sel={p.sel} id={p.id} title={p.title} class="mr-1" /> : <span class="mr-1 size-4 shrink-0" />}
      <span class="shrink-0" style={{ width: `${p.depth * INDENT}rem` }} />
    </>
  );
}

/** A line across the table at `depth`: loading, or why it couldn't. */
function Waiting({ depth, error }: { depth: number; error: string | undefined }) {
  return (
    <tr>
      <td colSpan={SPAN} class="py-2" style={padOf(depth)}>
        {error ? <span class="text-sm text-red-700">{error}</span> : <Spinner small={true} />}
      </td>
    </tr>
  );
}

/** One page of a folder's own items (those the filters match), the last offering the next. */
function ItemPage(p: {
  env: Env;
  folder: Around;
  depth: number;
  cursor: string | null;
  onMore: ((next: string) => void) | null;
}) {
  const { env } = p;
  const list = useResource(
    `folder-items:${env.space.id}:${p.folder.id}:${JSON.stringify(env.filters)}:${p.cursor}`,
    (s) =>
      api.get<PageItemRow>(
        spaceApi(env.space, "/items"),
        { ...env.filters, folder: p.folder.id, sort: "title", limit: STEP, cursor: p.cursor },
        s,
      ),
  );
  if (list.data === undefined) {
    return <Waiting depth={p.depth} error={list.error?.message} />;
  }
  const next = list.data.next;
  return (
    <>
      {list.data.data.map((row) => (
        <TreeNode
          key={row.id}
          space={env.space}
          entities={env.entities}
          path={[]}
          row={row}
          depth={p.depth}
          lead={<Lead sel={env.sel} id={row.id} title={titleOf(row)} depth={p.depth} />}
          selected={env.sel?.has(row.id) ?? false}
          dragging={env.sel?.has(row.id) ? env.sel.ids : undefined}
          part={false}
          after={<ItemDrift space={env.space} folder={p.folder} item={row} onDone={list.reload} />}
          draggable={canEdit(env.space)}
        />
      ))}
      {next && p.onMore ? (
        <tr>
          <td colSpan={SPAN} class="py-1" style={padOf(p.depth)}>
            <Button size="xs" variant="ghost" onClick={() => p.onMore?.(next)}>
              {t("items.tree.more")}
            </Button>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** What's in an open folder: its subfolders (holding matches), then its own items, a page at a time. */
function Contents({ env, folder, depth }: { env: Env; folder: Around; depth: number }) {
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const ask = folderAsk(env.filters, env.unmatched ?? false);
  const subs = useResource(`folder-subs:${env.space.id}:${folder.id}:${JSON.stringify(env.filters)}:${ask.key}`, (s) =>
    api.get<FolderList>(spaceApi(env.space, "/folders"), { ...env.filters, parent: folder.id, ...ask.query }, s),
  );
  if (subs.data === undefined) {
    return <Waiting depth={depth} error={subs.error?.message} />;
  }
  return (
    <>
      {ask.keep(subs.data.data).map((f) => (
        <FolderLine key={f.id} env={env} folder={f} around={folder} depth={depth} />
      ))}
      {pages.map((cursor, i) => (
        <ItemPage
          key={cursor ?? ""}
          env={env}
          folder={folder}
          depth={depth}
          cursor={cursor}
          onMore={i === pages.length - 1 ? (next) => setPages([...pages, next]) : null}
        />
      ))}
    </>
  );
}

/** What a folder row says beside its name: who sees it, how many matching items it holds, comments, drift. */
function FolderBits(p: { env: Env; folder: FolderRow; around: Around | undefined }) {
  const { env, folder } = p;
  return (
    <>
      <VisibilityMark item={folder} members={undefined} />
      {/* No items in it (subfolders aside): no count, rather than a 0 by a folder that opens. */}
      {folder.count > 0 ? (
        <Chip title={t("folders.itemsInside", { count: folder.count })}>{formatNumber(folder.count)}</Chip>
      ) : null}
      <FolderCommentCount space={env.space} id={folder.id} />
      <FolderDrift space={env.space} folder={p.around} sub={folder} onDone={env.onChange} />
    </>
  );
}

/** Whether a folder has anything to open onto (as far as the list's filters go). */
const holds = (f: FolderRow) => f.count > 0 || f.folders > 0;

/** Its chevron, or the room for one (nothing in it, or it can't be opened in place). */
function Toggle(p: { folder: FolderRow; open: boolean; can: boolean; onToggle: () => void }) {
  return p.can && holds(p.folder) ? (
    <Chevron open={p.open} label={t("folders.open", { name: p.folder.name })} onToggle={p.onToggle} />
  ) : (
    <span class="size-5 shrink-0" />
  );
}

/** A folder inside an open one: a line across the table, as an item's in a folder (or a group's) is. */
function FolderLine(p: { env: Env; folder: FolderRow; around: Around; depth: number }) {
  const { env, folder } = p;
  const [open, setOpen] = useState(false);
  const drop = useDropInto(env.space, folder.id, folder.name, !canEdit(env.space));
  return (
    <>
      <tr
        data-folder={folder.id}
        class={`${env.sel?.has(folder.id) ? SELECTED_ROW : ""} ${drop.over ? DROP_OVER : ""}`}
        draggable={canEdit(env.space)}
        onDragStart={(e) =>
          env.sel?.has(folder.id) ? dragIds(e, env.sel.ids) : dragIds(e, [folder.id], folder.parent ?? null)
        }
        {...drop.props}
      >
        <td colSpan={SPAN} class="py-1.5 pr-3 pl-3">
          <span class="flex min-w-0 items-center gap-1 text-sm">
            <Lead sel={env.sel} id={folder.id} title={folder.name} depth={p.depth} />
            <Toggle folder={folder} open={open} can={true} onToggle={() => setOpen(!open)} />
            <PeekButton
              space={env.space}
              id={folder.id}
              name={folder.name}
              icon={<FolderIcon class="text-zinc-500" />}
            />
            <Link
              href={folderHref(env.space, folder.id, location.value.searchParams)}
              class={`${LINK} min-w-0 truncate`}
            >
              {folder.name}
            </Link>
            <FolderBits env={env} folder={folder} around={p.around} />
            <span class="ml-auto shrink-0 text-xs text-zinc-500" title={formatInstant(folder.updated_at)}>
              {formatRelative(folder.updated_at)}
            </span>
          </span>
        </td>
      </tr>
      {open ? <Contents env={env} folder={folder} depth={p.depth + 1} /> : null}
    </>
  );
}

/**
 * A folder's row among the list's items, in their columns: its box (selected as they are), chevron (none when there's
 * nothing in it, or it can't be opened in place: a search's folder found by name), its name to its page, who sees it,
 * how many matching items it holds, its comments; `path` (a search's) where it is; when it last changed.
 */
function FolderRowLine(p: {
  env: Env;
  folder: FolderRow;
  /** The folder it's in (its sharing compared with this one's), when the list shows that folder. */
  around: Around | undefined;
  /** The columns between the title's and the last change's. */
  between: number;
  sel: Selection | undefined;
  /** Where it is (a search's folders, from anywhere). */
  path?: readonly Crumb[] | undefined;
  /** It can't be opened in place (found by name: its page lists it). */
  closed: boolean;
  /** It's dragged, and takes what's dropped on it (who may change things). */
  movable: boolean;
}) {
  const { env, folder, sel, movable } = p;
  const [open, setOpen] = useState(false);
  // Things dropped on it go into it; dragged, it takes the selection with it when it's selected.
  const drop = useDropInto(env.space, folder.id, folder.name, !movable);
  return (
    <>
      <tr
        class={`group${sel?.has(folder.id) ? ` ${SELECTED_ROW}` : ""}${drop.over ? ` ${DROP_OVER}` : ""}`}
        data-folder={folder.id}
        draggable={movable}
        onDragStart={
          movable
            ? (e) => (sel?.has(folder.id) ? dragIds(e, sel.ids) : dragIds(e, [folder.id], folder.parent ?? null))
            : undefined
        }
        {...drop.props}
      >
        <Td class="w-px pr-0">
          <span class="flex items-center gap-1">
            {sel ? <SelectBox sel={sel} id={folder.id} title={folder.name} class="mr-1" /> : null}
            <Toggle folder={folder} open={open} can={!p.closed} onToggle={() => setOpen(!open)} />
            <PeekButton
              space={env.space}
              id={folder.id}
              name={folder.name}
              icon={
                <span class="flex h-5 items-center">
                  <FolderIcon size={16} class="text-zinc-500" />
                </span>
              }
            />
          </span>
        </Td>
        <Td>
          <span class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <Link href={folderHref(env.space, folder.id, location.value.searchParams)} class={LINK}>
              {folder.name}
            </Link>
            <FolderBits env={env} folder={folder} around={p.around} />
          </span>
          {p.path && p.path.length > 1 ? (
            <Breadcrumb space={env.space} path={p.path.slice(0, -1)} small={true} class="mt-0.5" />
          ) : null}
        </Td>
        <td colSpan={p.between} />
        <Td title={formatInstant(folder.updated_at)}>{formatRelative(folder.updated_at)}</Td>
      </tr>
      {open ? <Contents env={env} folder={folder} depth={1} /> : null}
    </>
  );
}

/** The folders a list starts with (their own body, before the items'), in its columns. */
export function FolderRows(p: {
  env: Env;
  folders: readonly FolderRow[];
  around?: Around | undefined;
  /** The columns between the title's and the last change's. */
  between: number;
  sel?: Selection | undefined;
  /** Each one's breadcrumb (a search's, found anywhere). */
  pathOf?: (id: string) => readonly Crumb[];
  /** Dragged and dropped on (by default, where rows can be selected). */
  movable?: boolean;
}) {
  if (p.folders.length === 0) {
    return null;
  }
  return (
    <tbody class="divide-y divide-zinc-100 border-b border-zinc-100">
      {p.folders.map((f) => (
        <FolderRowLine
          key={f.id}
          env={{ ...p.env, sel: p.sel }}
          folder={f}
          around={p.around}
          between={p.between}
          sel={p.sel}
          path={p.pathOf?.(f.id)}
          closed={p.pathOf !== undefined}
          movable={p.movable ?? p.sel !== undefined}
        />
      ))}
    </tbody>
  );
}
